import './style/index.less';

import axios from 'axios';
import * as React from 'react';
import * as ReactDOM from 'react-dom';

import CountdownClock from './components/CountdownClock';
import Washroom, { BOARD_BY_ROUTE, LINE_COLOUR, LINE_TEXT } from './Washroom';
import { secondsPerDay } from './consts';
import { WEATHER_ICONS } from './weatherIcons';

export const NODE_ENV = process.env.NODE_ENV || 'development';

console.log('config', { NODE_ENV });

interface CountdownEvent {
    targetDate: Date,
    name: string,
    // Optional: the older half of events.json predates it, and the add-event
    // form doesn't ask for one. Absent just means no glyph, not a broken row.
    emoji?: string,
}

// The commute card: the train Google Maps says to take to the next calendar
// event that has a location, counted down against the MTA's live feed. Every
// field past `active` is absent when the card is off, so they're all optional.
interface CommuteData {
    active: boolean,
    // Why there's nothing to show, for debugging on a tablet with no console:
    // not_connected / nothing_soon / no_subway_route / error.
    reason?: string,
    event?: { name: string, location: string, start: string },
    route?: {
        route: string,
        direction: string,
        station: string,
        headsign: string,
        walkMinutes: number,
        totalMinutes: number,
    },
    // Absolute instant, so the countdown ticks in the browser between polls.
    trainAt?: string,
    trainMinutes?: number,
    leaveInMinutes?: number,
    arriveAt?: string,
    lateMinutes?: number,
    following?: number[],
}

interface DoordashData {
    orderInProgress: boolean,
    restaurant: string | null,
    statusText: string | null,
    step: number | null,
    stepCount: number | null,
    eta: string | null,
    // ISO timestamps from the scraper. placedAt is when the scraper first saw
    // the order rather than when it was actually placed (DoorDash never prints
    // the latter); etaStart/etaEnd are the two ends of the arrival window.
    placedAt: string | null,
    etaStart: string | null,
    etaEnd: string | null,
    etaText: string | null,
}

interface WeatherData {
    temperatureC: number | null,
    highC: number | null,
    lowC: number | null,
    precipChance: number | null,
    weatherCode: number | null,
    error: string | null,
}

interface IndoorData {
    tempF: number | null,
    humidity: number | null,
    co2: number | null,
    pm25: number | null,
}

interface LightData {
    id: string,
    label: string,
    on: boolean,
    primary: boolean,
}

// All Fahrenheit - the server converts at the Home Assistant boundary, so the
// UI never has to know or guess what unit system HA is set to.
interface ThermostatData {
    id: string,
    label: string,
    currentF: number | null,
    lowF: number | null,
    highF: number | null,
    targetF: number | null,
    minF: number | null,
    maxF: number | null,
    action: string,
}

// Present only once the Spotify integration is connected in HA; null otherwise,
// so the card simply doesn't render rather than showing an empty shell.
interface MediaData {
    id: string,
    state: string,
    title: string | null,
    artist: string | null,
    album: string | null,
    art: string | null,
    durationS: number | null,
    positionS: number | null,
    positionAt: string | null,
    // Spotify Connect target - which speaker is currently playing, and the
    // full list HA knows about right now.
    source: string | null,
    sources: string[],
    // open.spotify.com link for the current track, if HA reported one.
    contentUrl: string | null,
}

// ISO timestamps from HA's sun sensors - used to pick day vs night weather art.
interface SunTimes {
    nextRising: string | null,
    nextSetting: string | null,
}

interface HomeAssistantData {
    indoor: IndoorData | null,
    lights: LightData[],
    thermostats: ThermostatData[],
    media: MediaData | null,
    sun: SunTimes,
}

// HA reports the play position as of a timestamp rather than continuously, so
// the bar is extrapolated here instead of polling once a second.
function playedSeconds(media: MediaData, now: number): number | null {
    if (media.positionS === null) return null;
    if (media.state !== 'playing' || media.positionAt === null) return media.positionS;
    const since = (now - new Date(media.positionAt).getTime()) / 1000;
    if (!Number.isFinite(since) || since < 0) return media.positionS;
    const played = media.positionS + since;
    return media.durationS === null ? played : Math.min(played, media.durationS);
}

// mm:ss for the elapsed/duration readout next to the progress bar.
function formatTime(seconds: number): string {
    const total = Math.max(0, Math.round(seconds));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
}

// Wall-clock time for the ends of the delivery bar, e.g. "3:46 PM".
function formatClock(ms: number): string {
    const d = new Date(ms);
    const h = d.getHours() % 12 || 12;
    return `${h}:${d.getMinutes().toString().padStart(2, '0')} ${d.getHours() < 12 ? 'AM' : 'PM'}`;
}

// The arrival end of the delivery bar, rendered in the TABLET's timezone.
// DoorDash's own scraped string (dd.etaText / dd.eta) can't be trusted for
// this: the scraper strftimes it inside a headless browser running as this
// host, and the host is UTC - so an 11:37 PM arrival printed as "3:37AM" and
// the bar's two ends silently disagreed by four hours (the left end already
// came from a timestamp). etaStart/etaEnd are absolute instants, so formatting
// those here lets the browser resolve the offset wherever the page is shown.
// The scraped text stays as the fallback for the case where a scrape produced
// a string but no timestamp at all.
function formatEta(dd: DoordashData): string | null {
    const start = dd.etaStart !== null ? Date.parse(dd.etaStart) : NaN;
    const end = dd.etaEnd !== null ? Date.parse(dd.etaEnd) : NaN;
    if (!Number.isFinite(end)) return dd.etaText;
    if (!Number.isFinite(start) || start === end) return formatClock(end);
    // A window carries one meridiem when both ends share it - "11:20-11:37 PM"
    // rather than twice the width for the same information.
    const from = formatClock(start);
    const to = formatClock(end);
    return from.slice(-2) === to.slice(-2)
        ? `${from.slice(0, -3)}-${to}`
        : `${from} - ${to}`;
}

// FNV-1a - just needs to turn a track id/title into a stable 32-bit seed, not
// be cryptographically anything.
function hashString(s: string): number {
    let h = 2166136261;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}

// mulberry32 - small, deterministic, no dependency needed for what's just a
// per-track waveform shape.
function mulberry32(seed: number): () => number {
    let a = seed;
    return function next(): number {
        a |= 0;
        a = (a + 0x6D2B79F5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

// Deterministic per-track bar heights (18%-100%), each blended a bit with the
// previous bar so the shape reads as an amplitude envelope rather than static.
// Seeded from the track itself, so two different songs get two different-
// looking waveforms - the honest version of "reflects the song" available
// without real audio-analysis data (HA's Spotify integration exposes
// position/duration, not waveform/frequency data).
function waveformHeights(seed: string, count: number): number[] {
    const rand = mulberry32(hashString(seed));
    const heights: number[] = [];
    let prev = 0.5;
    for (let i = 0; i < count; i++) {
        const v = prev * 0.4 + rand() * 0.6;
        prev = v;
        heights.push(18 + v * 82);
    }
    return heights;
}

// hvac_action, shown as a glyph so the row reads at a glance.
function hvacIcon(action: string): string {
    if (action === 'cooling') return '❄️';
    if (action === 'heating') return '🔥';
    if (action === 'off') return '⏻';
    return '·';
}

interface FoodItem {
    item: string,
    kcal: number,
    meal: string | null,
}

interface AppState {
    events: CountdownEvent[],
    showingAddEventForm: boolean,
    eventName: string,
    eventTime: string,
    doordashData: DoordashData,
    commute: CommuteData,
    // Today's kcal from the Notion calorie log. null hides the chip: either
    // the first poll hasn't landed or Notion couldn't be read, and a 0 there
    // would read as "nothing eaten" rather than "don't know".
    eatenToday: number | null,
    // Today's log item by item, newest first. Keyed (the tablet only), so it
    // stays null on the public dashboard, which gets the total alone.
    foodToday: FoodItem[] | null,
    // How many food rows fit the list's measured height.
    foodCapacity: number,
    weather: WeatherData,
    ha: HomeAssistantData,
    // Slider positions in Fahrenheit while a drag is in flight, keyed by entity.
    thermoDraft: Record<string, number>,
    // The QR popover on the now-playing card. The speaker picker used to be a
    // second one of these; it's a native <select> now, so the OS owns that
    // state and there's nothing here to track.
    showQr: boolean,
    // Wall-clock, ticked once a second so anything that animates against real
    // time (Spotify's scrubber, the delivery bar) advances between polls.
    now: number,
}

const UPDATE_EVENTS_MS = 24*60*60*1000; // 1 day
const UPDATE_WEATHER_MS = 30*60*1000; // 30 minutes
// Spotify's now-playing card rides on this, and a minute was long enough that
// a track change or a scrub sat visibly wrong on the tablet. HA is on the LAN
// and /get-ha is a single cached fetch, so 12s costs little.
const UPDATE_HA_MS = 12*1000;
// Local re-render for the second-by-second bits (Spotify's scrubber, the
// delivery progress bar). Deliberately not a poll - position and ETA are both
// extrapolated from a timestamp we already have, so this only needs to make
// React look at the clock again. Gated in tick() to the hero that needs it.
const TICK_MS = 1000;
// A host-level scraper (outside this container) writes the order status file
// on its own schedule; this just needs to be faster than an order's status
// realistically changes, without hammering the server.
const UPDATE_DOORDASH_MS = 30*1000; // 30 seconds
// Matches the server's MTA cache, so a poll can actually return a new number.
const UPDATE_COMMUTE_MS = 30*1000;
// The server caches Notion for a minute, so polling faster can't show a new
// entry any sooner.
const UPDATE_CUT_MS = 60*1000;
// Rows the food list shows before its first measurement lands. After that it
// shows as many as its card has room for (see measureFood).
const FOOD_ROWS_DEFAULT = 8;
const MEAL_EMOJI: Record<string, string> = {
    Breakfast: '🍳',
    Lunch: '🥪',
    Dinner: '🍲',
    Snack: '🍪',
    Drink: '🥤',
};
const HA_RETRY_MS = 8*1000; // after a failed fetch, don't wait a whole poll
const THERMOSTAT_SETTLE_MS = 25*1000; // Nest round-trips through Google's cloud
const THERMOSTAT_COMMIT_MS = 450; // write once the drag settles, not per frame
const THERMO_MIN_F = 60;
const THERMO_MAX_F = 85;

// Only the tablet's start URL carries this key, so the publicly-reachable
// dashboard renders read-only and can't drive the lights.
const CONTROL_KEY = new URLSearchParams(window.location.search).get('k') || '';

// Is it currently dark out? HA gives the *next* sunrise and sunset, so
// whichever comes first tells us which side of the line we're on: if the sun
// is going to rise before it sets, it's down right now.
function isNight(sun: SunTimes): boolean {
    if (sun.nextRising === null || sun.nextSetting === null) return false;
    const rise = Date.parse(sun.nextRising);
    const set = Date.parse(sun.nextSetting);
    if (!Number.isFinite(rise) || !Number.isFinite(set)) return false;
    return rise < set;
}

// Maps WMO weather codes (used by Open-Meteo) to a Meteocons glyph name (see
// weatherIcons.ts) + a short label. Day/night variants where the set has
// them - swapping the sun for a moon after dark is most of what sells these
// as an actual weather picture rather than a generic symbol.
function weatherCodeToDisplay(code: number, night: boolean): { icon: string, desc: string } {
    const dn = (base: string) => `${base}-${night ? 'night' : 'day'}`;
    if (code === 0) return { icon: dn('clear'), desc: 'Clear' };
    if (code === 1 || code === 2) return { icon: dn('partly-cloudy'), desc: 'Partly Cloudy' };
    if (code === 3) return { icon: dn('overcast'), desc: 'Overcast' };
    if (code === 45 || code === 48) return { icon: dn('fog'), desc: 'Fog' };
    if (code >= 51 && code <= 55) return { icon: 'drizzle', desc: 'Drizzle' };
    if (code === 56 || code === 57) return { icon: 'sleet', desc: 'Freezing Drizzle' };
    if (code >= 61 && code <= 65) return { icon: 'rain', desc: 'Rain' };
    if (code === 66 || code === 67) return { icon: 'sleet', desc: 'Freezing Rain' };
    if (code >= 71 && code <= 77) return { icon: 'snow', desc: 'Snow' };
    if (code >= 80 && code <= 82) return { icon: dn('partly-cloudy') + '-rain', desc: 'Showers' };
    if (code >= 85 && code <= 86) return { icon: dn('partly-cloudy') + '-snow', desc: 'Snow Showers' };
    if (code >= 95) return { icon: dn('thunderstorms'), desc: 'Thunderstorm' };
    return { icon: 'not-available', desc: '' };
}

// The glyphs are raw markup rather than components - see weatherIcons.ts for
// why (SMIL animation + the WebView's masked-SVG colour bug).
function WeatherIcon(props: { code: number, night: boolean }): JSX.Element {
    const { icon } = weatherCodeToDisplay(props.code, props.night);
    const svg = WEATHER_ICONS[icon] || WEATHER_ICONS['not-available'];
    return <div className="weather-card-icon" dangerouslySetInnerHTML={{ __html: svg }} />;
}

const DEFAULT_EVENT_NAME = '';

// Local datetime string (YYYY-MM-DDTHH:mm) for the <input type="datetime-local">,
// defaulted to noon tomorrow so the picker always opens on a sensible future date.
function defaultEventTime(): string {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(12, 0, 0, 0);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Plain inline <svg> with fill="currentColor", rather than the CSS mask-image
// technique the rest of this file uses for glyphs - the tablet's WebView has a
// documented color bug for masked/rasterized SVGs, but live DOM SVG paths take
// a different rendering path and pick up the pink from the surrounding CSS.
function IconPrev(props: { className?: string }): JSX.Element {
    return (
        <svg className={props.className} viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
        </svg>
    );
}

function IconNext(props: { className?: string }): JSX.Element {
    return (
        <svg className={props.className} viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
        </svg>
    );
}

function IconPlay(props: { className?: string }): JSX.Element {
    return (
        <svg className={props.className} viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
        </svg>
    );
}

function IconPause(props: { className?: string }): JSX.Element {
    return (
        <svg className={props.className} viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
        </svg>
    );
}

function IconCast(props: { className?: string }): JSX.Element {
    return (
        <svg className={props.className} viewBox="0 0 24 24" fill="currentColor">
            <path d="M21,3H3C1.9,3,1,3.9,1,5v3h2V5h18v14h-7v2h7c1.1,0,2-0.9,2-2V5C23,3.9,22.1,3,21,3z
                M1,18v3h3C4,19.34,2.66,18,1,18z M1,14v2c2.76,0,5,2.24,5,5h2C8,17.13,4.87,14,1,14z
                M1,10v2c4.97,0,9,4.03,9,9h2C12,15.93,7.07,10,1,10z" />
        </svg>
    );
}

function IconQr(props: { className?: string }): JSX.Element {
    return (
        <svg className={props.className} viewBox="0 0 24 24" fill="currentColor">
            <path d="M9.5,6.5v3h-3v-3H9.5 M11,5H5v6h6V5L11,5z M9.5,14.5v3h-3v-3H9.5 M11,13H5v6h6V13L11,13z
                M17.5,6.5v3h-3v-3H17.5 M19,5h-6v6h6V5L19,5z M13,13h1.5v1.5H13V13z M14.5,14.5H16V16h-1.5V14.5z
                M16,13h1.5v1.5H16V13z M13,16h1.5v1.5H13V16z M14.5,17.5H16V19h-1.5V17.5z M16,16h1.5v1.5H16V16z
                M17.5,14.5H19V16h-1.5V14.5z M17.5,17.5H19V19h-1.5V17.5z" />
        </svg>
    );
}

class App extends React.Component<Record<string, never>, AppState> {
    private thermoCommitTimers: Record<string, ReturnType<typeof setTimeout>> = {};

    constructor(props : Record<string, never>) {
        super(props);

        this.state = {
            events: [],
            showingAddEventForm: false,
            eventName: DEFAULT_EVENT_NAME,
            eventTime: defaultEventTime(),
            doordashData: {
                orderInProgress: false,
                restaurant: null,
                statusText: null,
                step: null,
                stepCount: null,
                eta: null,
                placedAt: null,
                etaStart: null,
                etaEnd: null,
                etaText: null,
            },
            weather: {
                temperatureC: null,
                highC: null,
                lowC: null,
                precipChance: null,
                weatherCode: null,
                error: null,
            },
            ha: {
                indoor: null,
                lights: [],
                thermostats: [],
                media: null,
                sun: { nextRising: null, nextSetting: null },
            },
            commute: { active: false },
            eatenToday: null,
            foodToday: null,
            foodCapacity: FOOD_ROWS_DEFAULT,
            thermoDraft: {},
            showQr: false,
            now: Date.now(),
        };

        this.resetAddEventForm = this.resetAddEventForm.bind(this);
        this.fetchEvents = this.fetchEvents.bind(this);
        this.addEvent = this.addEvent.bind(this);
        this.fetchWeather = this.fetchWeather.bind(this);
        this.fetchDoordash = this.fetchDoordash.bind(this);
        this.fetchCommute = this.fetchCommute.bind(this);
        this.fetchCut = this.fetchCut.bind(this);
        this.renderFood = this.renderFood.bind(this);
        this.renderCommute = this.renderCommute.bind(this);
        this.fetchHomeAssistant = this.fetchHomeAssistant.bind(this);
        this.slideThermostat = this.slideThermostat.bind(this);
        this.commitThermostat = this.commitThermostat.bind(this);
        this.mediaCommand = this.mediaCommand.bind(this);
        this.selectSource = this.selectSource.bind(this);
        this.toggleQr = this.toggleQr.bind(this);
        this.renderNowPlaying = this.renderNowPlaying.bind(this);
        this.renderWaveform = this.renderWaveform.bind(this);
    }

    componentDidMount(): void {
        this.fetchEvents();
        setInterval(() => this.fetchEvents(), UPDATE_EVENTS_MS);
        this.fetchWeather();
        setInterval(() => this.fetchWeather(), UPDATE_WEATHER_MS);
        this.fetchHomeAssistant();
        setInterval(() => this.fetchHomeAssistant(), UPDATE_HA_MS);
        this.fetchDoordash();
        setInterval(() => this.fetchDoordash(), UPDATE_DOORDASH_MS);
        this.fetchCommute();
        setInterval(() => this.fetchCommute(), UPDATE_COMMUTE_MS);
        this.fetchCut();
        setInterval(() => this.fetchCut(), UPDATE_CUT_MS);
        setInterval(() => this.tick(), TICK_MS);
    }

    // Advance the clock only while something on screen is actually animating
    // against it. Otherwise this would re-render 48 waveform bars and an
    // SMIL-animated weather glyph once a second for nothing, all day, on a
    // tablet - the countdown clocks run their own timers and don't need it.
    tick(): void {
        const media = this.state.ha.media;
        const needed = this.state.doordashData.orderInProgress
            || this.state.commute.active
            || (media !== null && media.state === 'playing');
        if (needed) this.setState({ now: Date.now() });
    }

    fetchHomeAssistant(): void {
        axios.get('/get-ha')
            .then(response => this.setState({ ha: response.data }))
            .catch(error => {
                // Expected when HA is unreachable or no token is configured -
                // the cards stay hidden rather than showing a broken state.
                // The server's first call after a redeploy sometimes races the
                // host gateway coming up, so retry well before the next poll
                // instead of leaving the card blank for a full minute.
                console.error('HA unavailable', error.toString());
                setTimeout(this.fetchHomeAssistant, HA_RETRY_MS);
            });
    }

    mediaCommand(command: string): void {
        if (CONTROL_KEY === '') return;
        const media = this.state.ha.media;
        if (media === null) return;
        axios.post('/ha/media/command', { id: media.id, command, key: CONTROL_KEY })
            // Spotify takes a beat to report the new track, so re-poll shortly
            // after rather than only immediately.
            .then(() => {
                this.fetchHomeAssistant();
                setTimeout(this.fetchHomeAssistant, 1200);
            })
            .catch(error => console.error(error));
    }

    // Redirect Spotify Connect to a different speaker. `source` always comes
    // from media.sources (what HA just reported), never free text - the
    // <option> values are built from that list, and anything else is dropped.
    selectSource(source: string): void {
        if (CONTROL_KEY === '') return;
        const media = this.state.ha.media;
        if (media === null) return;
        if (source === '' || !media.sources.includes(source)) return;
        axios.post('/ha/media/source', { id: media.id, source, key: CONTROL_KEY })
            .then(() => {
                this.fetchHomeAssistant();
                setTimeout(this.fetchHomeAssistant, 1200);
            })
            .catch(error => console.error(error));
    }

    toggleQr(): void {
        this.setState(state => ({ showQr: !state.showQr }));
    }

    // Dragging updates a local draft so the slider tracks the finger with no
    // round trip; the thermostat is only written once the drag settles.
    slideThermostat(id: string, centre: number): void {
        if (CONTROL_KEY === '') return;
        this.setState(state => ({ thermoDraft: { ...state.thermoDraft, [id]: centre } }));

        if (this.thermoCommitTimers[id]) clearTimeout(this.thermoCommitTimers[id]);
        this.thermoCommitTimers[id] = setTimeout(() => this.commitThermostat(id), THERMOSTAT_COMMIT_MS);
    }

    commitThermostat(id: string): void {
        const centre = this.state.thermoDraft[id];
        if (centre === undefined) return;

        axios.post('/ha/climate/set', { id, centreF: centre, key: CONTROL_KEY })
            .then(() => {
                // Drop the draft so the server's pending value takes over, then
                // poll again once Google's cloud has caught up.
                this.setState(state => {
                    const next = { ...state.thermoDraft };
                    delete next[id];
                    return { thermoDraft: next };
                });
                this.fetchHomeAssistant();
                setTimeout(this.fetchHomeAssistant, THERMOSTAT_SETTLE_MS);
            })
            .catch(error => console.error(error));
    }

    fetchWeather(): void {
        axios.get('/get-weather')
            .then(response => {
                this.setState({ weather: { ...response.data, error: null } });
            })
            .catch(error => {
                console.error(error);
                this.setState(state => ({ weather: { ...state.weather, error: error.toString() } }));
            });
    }

    resetAddEventForm(): void {
        this.setState({
            showingAddEventForm: false,
            eventName: DEFAULT_EVENT_NAME,
            eventTime: defaultEventTime(),
         });
    }

    fetchEvents(): void {
        axios.get('/get-events')
            .then(response => {
                this.setState(
                    { events:
                        response.data.map(event => ({
                            targetDate: new Date(event.targetDate),
                            name: event.name,
                            emoji: event.emoji,
                        })) },
                    () => this.resetAddEventForm());
            })
            .catch(error => console.error(error));
    }

    addEvent(event: CountdownEvent): void {
        axios.post('/add-event', { 'event': event })
            .then(response => this.fetchEvents())
            .catch(error => console.error(error));
    }

    // Order data itself comes from a host-level Playwright scraper (outside
    // this container) that stays logged into DoorDash and watches the
    // account's own orders list - see /get-doordash in index.js. This just
    // polls that cache; it never talks to DoorDash directly.
    fetchDoordash(): void {
        axios.get('/get-doordash')
            .then(response => this.setState({ doordashData: response.data }))
            .catch(error => console.error(error));
    }

    fetchCommute(): void {
        axios.get('/get-commute')
            .then(response => this.setState({ commute: response.data }))
            // A 500 here means Google or the MTA fell over; the card just stays
            // hidden rather than freezing on a stale countdown.
            .catch(() => this.setState({ commute: { active: false, reason: 'error' } }));
    }

    // Header total from /get-cut either way; the rows only when the keyed
    // fetch landed. The total stays authoritative even with rows showing, so
    // the two can't visibly disagree mid-refresh.
    renderFood(total: number): JSX.Element {
        const items = this.state.foodToday ?? [];
        const capacity = this.state.foodCapacity;
        // Newest first; when they don't all fit, the oldest fold into a
        // "+ N earlier" line, which takes the last row's slot.
        const shown = items.length <= capacity ? items : items.slice(0, Math.max(capacity - 1, 0));
        const hidden = items.length - shown.length;
        return (
            <div className="food-card">
                <div className="panel-head">
                    <span className="panel-label">EATEN</span>
                    <span className="food-total">{total.toLocaleString('en-US')} kcal</span>
                </div>
                <div className="food-list" ref={this.foodListRef}>
                    {shown.map((food, i) =>
                        <div className="food-row" key={`${i}-${food.item}`}>
                            <span className="food-emoji">{MEAL_EMOJI[food.meal ?? ''] ?? '🍽️'}</span>
                            <span className="food-name">{food.item}</span>
                            <span className="food-kcal">{food.kcal}</span>
                        </div>)}
                    {hidden > 0 &&
                        <div className="food-more">+ {hidden} earlier</div>}
                </div>
            </div>
        );
    }

    // The list's height comes from the flex layout, never from its rows, so
    // re-rendering with a different count can't feed back into the measurement.
    private foodObserver: ResizeObserver | null = null;
    private foodList: HTMLDivElement | null = null;

    foodListRef = (el: HTMLDivElement | null): void => {
        this.foodObserver?.disconnect();
        this.foodObserver = null;
        this.foodList = el;
        if (el === null) return;
        this.foodObserver = new ResizeObserver(() => this.measureFood());
        this.foodObserver.observe(el);
    };

    measureFood(): void {
        const el = this.foodList;
        if (el === null) return;
        // A row is one line of the list's own font; read it off the element so
        // a font-size change in the stylesheet doesn't need a matching number here.
        const rowPx = parseFloat(getComputedStyle(el).lineHeight);
        if (!(rowPx > 0)) return;
        const capacity = Math.max(1, Math.floor(el.clientHeight / rowPx));
        if (capacity !== this.state.foodCapacity) this.setState({ foodCapacity: capacity });
    }

    // Shares the washroom's /get-cut. today.eaten is null both when Notion
    // failed and when there are simply no entries yet, so source tells them apart.
    fetchCut(): void {
        axios.get('/get-cut')
            .then(response => this.setState({
                eatenToday: response.data.source === 'none' ? null : (response.data.today.eaten ?? 0),
            }))
            .catch(() => this.setState({ eatenToday: null }));
        if (CONTROL_KEY === '') return;
        axios.get('/get-food-today', { headers: { 'X-Control-Key': CONTROL_KEY } })
            .then(response => this.setState({ foodToday: response.data.items }))
            .catch(() => this.setState({ foodToday: null }));
    }

    // Bar heights are a per-track fingerprint (seeded from the track itself,
    // see waveformHeights above), and bars up to the current play position are
    // lit solid pink like a scrubber - so the shape and the fill both actually
    // track the song playing, even though there's no real amplitude data
    // behind it. The gentle bounce is CSS (animation-delay per bar in
    // index.less), frozen while paused.
    renderWaveform(media: MediaData, playing: boolean, pct: number | null): JSX.Element {
        const seed = media.contentUrl || `${media.title || ''}|${media.artist || ''}` || media.id;
        const barCount = 48;
        const heights = waveformHeights(seed, barCount);
        const playedBars = pct !== null ? Math.round((pct / 100) * barCount) : 0;
        return (
            <div className={`np-wave${playing ? '' : ' paused'}`}>
                {heights.map((h, i) =>
                    <div
                        key={i}
                        className={`np-wave-bar${i < playedBars ? ' played' : ''}`}
                        style={{ height: `${h}%`, animationDelay: `${(i * -0.083).toFixed(3)}s` }} />)}
            </div>
        );
    }

    // Nothing at all when Spotify is idle - a permanent empty player would be
    // clutter on a screen that is mostly glanced at. When it IS active, this
    // takes over the clock's real estate entirely (see render()) rather than
    // squeezing into the control column.
    renderNowPlaying(): JSX.Element | null {
        const media = this.state.ha.media;
        if (media === null) return null;
        if (media.state !== 'playing' && media.state !== 'paused') return null;

        // this.state.now, not Date.now(): position is extrapolated from HA's
        // last report, so it only actually moves when something re-renders.
        // The ticker in componentDidMount is that something.
        const played = playedSeconds(media, this.state.now);
        const pct = played !== null && media.durationS
            ? Math.max(0, Math.min(100, (played / media.durationS) * 100))
            : null;
        const playing = media.state === 'playing';

        return (
            <div className={`np${playing ? '' : ' paused'}`}>
                <div className="np-main">
                    {media.art !== null &&
                        <img className="np-art" src={media.art} alt="" />}
                    <div className="np-text">
                        <div className="np-title">{media.title || 'Spotify'}</div>
                        <div className="np-artist">{media.artist || ''}</div>
                        {pct !== null &&
                            <div className="np-track">
                                <div className="np-played" style={{ width: `${pct}%` }} />
                            </div>}
                        {played !== null && media.durationS !== null &&
                            <div className="np-time">
                                <span>{formatTime(played)}</span>
                                <span>{formatTime(media.durationS)}</span>
                            </div>}
                    </div>
                </div>
                {this.renderWaveform(media, playing, pct)}
                {/* Transport and the cast/join pills share one row - stacked,
                    they were two mostly-empty bands in a card that's already
                    tight for height. */}
                <div className="np-controls">
                {CONTROL_KEY !== '' &&
                    <div className="np-buttons">
                        <div className="np-btn" onClick={() => this.mediaCommand('previous')}>
                            <IconPrev className="np-icon" />
                        </div>
                        <div className="np-btn play" onClick={() => this.mediaCommand('play_pause')}>
                            {playing ? <IconPause className="np-icon" /> : <IconPlay className="np-icon" />}
                        </div>
                        <div className="np-btn" onClick={() => this.mediaCommand('next')}>
                            <IconNext className="np-icon" />
                        </div>
                    </div>}
                <div className="np-toolbar">
                    {CONTROL_KEY !== '' && media.sources.length > 0 &&
                        <div className="np-tool np-tool-native">
                            <IconCast className="np-icon" />
                            <span>{media.source || 'Speaker'}</span>
                            {/* A transparent native <select> sits on top of the
                                pill, so tapping the pill really taps the select
                                and Android puts up its own speaker list. The
                                in-page popover this replaced had to reinvent
                                scrolling and hit targets on a wall tablet; the
                                OS one is already tuned for a finger. Opening a
                                <select> from script isn't portable, hence the
                                overlay rather than a click handler. */}
                            <select
                                className="np-native-select"
                                value={media.source && media.sources.includes(media.source)
                                    ? media.source : ''}
                                onChange={e => this.selectSource(e.target.value)}>
                                {/* Placeholder for "HA reports a source we
                                    weren't offered", so the select still has a
                                    matching value and doesn't silently show
                                    the first speaker as current. */}
                                {!(media.source && media.sources.includes(media.source)) &&
                                    <option value="">{media.source || 'Speaker'}</option>}
                                {media.sources.map(source =>
                                    <option key={source} value={source}>{source}</option>)}
                            </select>
                        </div>}
                    {media.contentUrl !== null &&
                        <div
                            className={`np-tool${this.state.showQr ? ' active' : ''}`}
                            onClick={this.toggleQr}>
                            <IconQr className="np-icon" />
                            <span>Join</span>
                        </div>}
                </div>
                </div>
                {this.state.showQr && media.contentUrl !== null &&
                    <div className="np-extra">
                        <img className="np-qr" src="/ha/media/qr" alt="QR code for this track" />
                        <div className="np-qr-caption">Scan to open this track in Spotify</div>
                    </div>}
            </div>
        );
    }

    // The commute hero. Outranks everything else in the top slot while it's
    // up, because it's the only card on this screen with a hard deadline you
    // can miss by standing here reading it - and it takes itself down the
    // moment the train is unreachable, so it never squats on the slot.
    renderCommute(): JSX.Element | null {
        const c = this.state.commute;
        if (!c.active || !c.route || !c.event || !c.trainAt) return null;

        const board = BOARD_BY_ROUTE[c.route.route] || 'irt';
        // Counted from the absolute instant, so it runs down every second
        // rather than stepping a whole minute each poll.
        const secs = Math.max(0, Math.round((Date.parse(c.trainAt) - this.state.now) / 1000));
        const mm = Math.floor(secs / 60);
        const ss = (secs % 60).toString().padStart(2, '0');

        const walk = c.route.walkMinutes;
        const leave = c.leaveInMinutes ?? 0;
        // The walk should already have started. Saying "leave in -2 min" is
        // useless; saying "go now" is not.
        const goNow = leave <= 0;

        return (
            <div className="commute">
                <div className="commute-head">
                    <span
                        className="commute-bullet"
                        style={{ background: LINE_COLOUR[board], color: LINE_TEXT[board] }}>
                        {c.route.route}
                    </span>
                    <span className="commute-station">{c.route.station}</span>
                    {c.route.headsign !== '' &&
                        <span className="commute-headsign">to {c.route.headsign}</span>}
                </div>

                <div className="commute-countdown">
                    {/* Minutes and seconds are sized apart deliberately: a plain
                        "9:18" in one weight reads as a wall-clock time, which is
                        exactly the wrong thing on a card that also prints an
                        arrival time a few lines down. */}
                    <span className="commute-mins">{mm}</span>
                    <span className="commute-secs">:{ss}</span>
                    <span className="commute-until">until the {c.route.route} arrives</span>
                </div>

                <div className={`commute-leave${goNow ? ' now' : ''}`}>
                    {goNow
                        ? <>🏃 Leave now — {walk} min walk</>
                        : <>🚶 Leave in <b>{leave}</b> min — {walk} min walk</>}
                </div>

                <div className="commute-event">
                    <span className="commute-event-name">{c.event.name}</span>
                    <span className="commute-event-where">{c.event.location}</span>
                </div>

                <div className="commute-foot">
                    {c.arriveAt &&
                        <span className={c.lateMinutes ? 'commute-late' : ''}>
                            {c.lateMinutes
                                ? `Arrives ${formatClock(Date.parse(c.arriveAt))} — ${c.lateMinutes} min late`
                                : `Arrives ${formatClock(Date.parse(c.arriveAt))}`}
                        </span>}
                    {c.following && c.following.length > 0 &&
                        <span className="commute-following">then {c.following.join(', ')} min</span>}
                </div>
            </div>
        );
    }

    // The delivery hero. Takes the whole countdown slot while an order is out
    // - a delivery is short-lived and time-critical, and it's the one thing on
    // this screen people actually stand and wait on, so it outranks both the
    // clocks and Spotify (which comes back the moment the food lands).
    renderDelivery(): JSX.Element | null {
        const dd = this.state.doordashData;
        if (!dd.orderInProgress) return null;

        const placed = dd.placedAt !== null ? Date.parse(dd.placedAt) : NaN;
        const arrival = dd.etaEnd !== null ? Date.parse(dd.etaEnd) : NaN;
        const haveBar = Number.isFinite(placed) && Number.isFinite(arrival) && arrival > placed;

        // Clamped at both ends: a dasher running late would otherwise push the
        // fill past 100%, and a placedAt in the future (clock skew) below 0.
        const pct = haveBar
            ? Math.max(0, Math.min(100, ((this.state.now - placed) / (arrival - placed)) * 100))
            : 0;
        // Once it's overdue, counting up past "0 min" reads as broken, so the
        // label switches to "Any minute now" rather than showing a negative.
        const minutesLeft = haveBar ? Math.ceil((arrival - this.state.now) / 60000) : null;
        const overdue = minutesLeft !== null && minutesLeft <= 0;

        // Without an ETA there is still something honest to show: DoorDash's
        // own "Step 3 out of 4" is a coarse progress signal, and time since
        // the order was placed answers "how long has this been going". That
        // beats the hero rendering nothing but a status line, which is what
        // it did on every order up to 2026-08-19 - the scraper was looking
        // for the ETA in a section that doesn't contain one, so haveBar was
        // never true in practice, only in the stubbed screenshots.
        const haveSteps = !haveBar && dd.step !== null && dd.stepCount !== null && dd.stepCount > 0;
        const barPct = haveBar ? pct
            : haveSteps ? Math.max(0, Math.min(100, (dd.step! / dd.stepCount!) * 100))
            : 0;
        const elapsed = Number.isFinite(placed)
            ? Math.max(0, Math.round((this.state.now - placed) / 60000))
            : null;
        const etaLabel = formatEta(dd);

        return (
            <div className="delivery">
                <div className="delivery-head">
                    <span className="delivery-label">🛵 DoorDash</span>
                    {dd.step !== null && dd.stepCount !== null &&
                        <span className="delivery-step">Step {dd.step} of {dd.stepCount}</span>}
                </div>
                {dd.restaurant !== null &&
                    <div className="delivery-restaurant">{dd.restaurant}</div>}
                {dd.statusText !== null &&
                    <div className="delivery-status">{dd.statusText}</div>}
                {haveBar &&
                    <div className="delivery-countdown">
                        {overdue ? 'Any minute now' : <><span className="delivery-mins">{minutesLeft}</span> min away</>}
                    </div>}
                {!haveBar && haveSteps &&
                    <div className="delivery-countdown">
                        {etaLabel !== null
                            ? <>ETA {etaLabel}</>
                            : elapsed !== null
                                ? <><span className="delivery-mins">{elapsed}</span> min in</>
                                : <>On the way</>}
                    </div>}
                {(haveBar || haveSteps) &&
                    <div className="delivery-bar">
                        <div className="delivery-bar-fill" style={{ width: `${barPct}%` }}>
                            <span className="delivery-scooter">🛵</span>
                        </div>
                    </div>}
                {haveBar &&
                    <div className="delivery-ends">
                        <span>{formatClock(placed)}</span>
                        <span>{etaLabel !== null ? etaLabel : formatClock(arrival)}</span>
                    </div>}
                {!haveBar && haveSteps &&
                    <div className="delivery-ends">
                        <span>{Number.isFinite(placed) ? formatClock(placed) : 'Ordered'}</span>
                        <span>Arriving soon</span>
                    </div>}
                {!haveBar && !haveSteps && etaLabel !== null &&
                    <div className="delivery-countdown">ETA {etaLabel}</div>}
            </div>
        );
    }

    render(): JSX.Element {
        const now = new Date();
        const aWeekAgo = new Date(now.getTime() - 7 * secondsPerDay * 1000);

        const events = this.state.events
            .filter(event => event.targetDate > aWeekAgo)
            .sort((a, b) => a.targetDate.getTime() - b.targetDate.getTime());

        // The climate card only exists with the control key (the sliders write
        // to Nest) and at least one thermostat reporting.
        const thermostats = CONTROL_KEY === ''
            ? []
            : this.state.ha.thermostats.filter(t => t.currentF !== null);

        const media = this.state.ha.media;
        // One hero at a time in the big top slot, in priority order: a train
        // to catch, then a live delivery, then Spotify, then the countdown
        // clocks. The first three are transient and time-sensitive; the clocks
        // are the resting state and are always the thing that yields.
        // Commute first: a train you can still catch is the only one of these
        // that stops being actionable if you look at it a minute later.
        const showCommuteHero = this.state.commute.active;
        const showDeliveryHero = !showCommuteHero && this.state.doordashData.orderInProgress;
        const showNowPlayingHero = !showCommuteHero && !showDeliveryHero && media !== null
            && (media.state === 'playing' || media.state === 'paused');
        const showClocks = !showCommuteHero && !showDeliveryHero && !showNowPlayingHero;

        return (
            <div className="app">
                <div className="content">
                    {!this.state.showingAddEventForm && showCommuteHero &&
                        <div className="commute-hero-wrapper">
                            {this.renderCommute()}
                        </div>}
                    {!this.state.showingAddEventForm && showDeliveryHero &&
                        <div className="delivery-hero-wrapper">
                            {this.renderDelivery()}
                        </div>}
                    {!this.state.showingAddEventForm && showNowPlayingHero &&
                        <div className="np-hero-wrapper">
                            {this.renderNowPlaying()}
                        </div>}
                    {!this.state.showingAddEventForm && showClocks &&
                        <div className="clock-wrapper">
                            {events.map((event, i) =>
                                <CountdownClock
                                    key={i}
                                    index={i}
                                    targetDate={event.targetDate}
                                    eventName={event.name}
                                    emoji={event.emoji}
                                    onTimeUpdate={(lastTime: number, time: number) => null} />
                            )}
                        </div>}
                    {!this.state.showingAddEventForm &&
                        <div className="bottom-row">
                            {(thermostats.length > 0 || this.state.eatenToday !== null) &&
                            <div className="bottom-stack">
                            {thermostats.length > 0 &&
                            <div className="climate-card">
                                <div className="panel-head">
                                    <span className="panel-label">CLIMATE</span>
                                </div>
                                <div className="panel-body">
                                    {thermostats.map(thermostat => {
                                        // Band centre is what the slider drives; a draft wins
                                        // while the finger is still down.
                                        const centreF = thermostat.lowF !== null && thermostat.highF !== null
                                            ? (thermostat.lowF + thermostat.highF) / 2
                                            : thermostat.targetF;
                                        const draft = this.state.thermoDraft[thermostat.id];
                                        const setF = draft !== undefined
                                            ? draft
                                            : centreF !== null ? Math.round(centreF) : null;
                                        if (setF === null) return null;

                                        return (
                                            <div className="thermo" key={thermostat.id}>
                                                <span className="thermo-label">
                                                    {thermostat.label} {hvacIcon(thermostat.action)}
                                                </span>
                                                <input
                                                    className="thermo-slider"
                                                    type="range"
                                                    min={THERMO_MIN_F}
                                                    max={THERMO_MAX_F}
                                                    step={1}
                                                    value={setF}
                                                    onChange={event =>
                                                        this.slideThermostat(thermostat.id, parseInt(event.target.value, 10))} />
                                                <span className="thermo-temps">
                                                    <span className="thermo-current">
                                                        {Math.round(thermostat.currentF)}°
                                                    </span>
                                                    <span className="thermo-set">{setF}°</span>
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>}
                            {this.state.eatenToday !== null && this.renderFood(this.state.eatenToday)}
                            </div>}
                            {this.state.weather.temperatureC !== null &&
                                <div className="weather-card">
                                    <div className="panel-head">
                                        <span className="panel-label">WEATHER</span>
                                    </div>
                                    <div className="weather-body">
                                    <div className="weather-outdoor">
                                        <WeatherIcon
                                            code={this.state.weather.weatherCode !== null ? this.state.weather.weatherCode : -1}
                                            night={isNight(this.state.ha.sun)} />
                                        <div className="weather-card-text">
                                            <div className="weather-card-temp">{this.state.weather.temperatureC}°C</div>
                                            <div className="weather-card-highlow">H:{this.state.weather.highC}° L:{this.state.weather.lowC}°</div>
                                            <div className="weather-card-precip">☔ {this.state.weather.precipChance}%</div>
                                        </div>
                                    </div>
                                    {this.state.ha.indoor !== null && this.state.ha.indoor.tempF !== null &&
                                        <div className="indoor-block">
                                            <div className="indoor-label">INSIDE</div>
                                            <div className="indoor-stat">{Math.round(this.state.ha.indoor.tempF)}°F</div>
                                            <div className="indoor-stat">💧 {Math.round(this.state.ha.indoor.humidity)}%</div>
                                            <div className="indoor-stat">CO₂ {Math.round(this.state.ha.indoor.co2)}</div>
                                            <div className="indoor-stat">PM2.5 {Math.round(this.state.ha.indoor.pm25)}</div>
                                        </div>}
                                    </div>
                                </div>}
                        </div>}
                </div>
            </div>
        );
    }
}

// One bundle, two screens: the kitchen dashboard and the washroom glance page.
ReactDOM.render(
    window.location.pathname.replace(/\/+$/, '') === '/washroom' ? <Washroom /> : <App />,
    document.getElementById('app')
);

// Pick up deploys without anyone touching the tablet. The washroom tablet loses
// its adb channel at every reboot and has no Fully Remote Admin (that's a PLUS
// feature), so a running page has to notice new code on its own. Polling a tiny
// token beats Fully's blind timed reload: the screen only blinks when there is
// actually something new to show.
const BUILD_POLL_MS = 30 * 1000;
let loadedBuild: string | null = null;

function checkForNewBuild(): void {
    axios.get('/build-token', {
        headers: { 'Cache-Control': 'no-cache' },
        params: { have: loadedBuild || '' },
    })
        .then(r => {
            const token = String(r.data).trim();
            if (!token) return;
            if (loadedBuild === null) {
                loadedBuild = token;
            } else if (token !== loadedBuild) {
                // New server build -- take the new bundle. `true` is ignored by
                // modern browsers but harmless on this tablet's older WebView.
                window.location.reload();
            }
        })
        .catch(() => { /* offline or mid-deploy; try again next tick */ });
}

checkForNewBuild();
setInterval(checkForNewBuild, BUILD_POLL_MS);
